import { observer } from "mobx-react-lite";
import useRestricted from "../hooks/useRestricted";
import { Permission } from "../services/UserService";
import { useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { AppContext } from "../contexts/AppContext";
import React from "react";
import { PlanItem } from "../components/plans/PlanItem";
import { AddOutlined } from "@mui/icons-material";
import { useIntl } from "react-intl";
import InfiniteScroll from "react-infinite-scroll-component";
import Logo from "../components/common/Logo";
import useTheme from "@mui/material/styles/useTheme";
import useMediaQuery from "@mui/material/useMediaQuery";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Snackbar from "@mui/material/Snackbar";
import posthog from "posthog-js";
import SurveyDialog from "../components/plans/SurveyDialog";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";

const Home: React.FC = observer(() => {

  const permission = useRestricted();

  const intl = useIntl();

  const navigate = useNavigate();

  const theme = useTheme();
  const isUpMD = useMediaQuery(theme.breakpoints.up('md'));

  const { plansService, userService, surveyService, reviewService } = React.useContext(AppContext);

  const [loadingStarted, setLoadingStarted] = React.useState(false);
  const [showSurveyThankYou, setShowSurveyThankYou] = React.useState(false);
  const [planFilter, setPlanFilter] = React.useState<'mine' | 'shared'>('mine');

  useEffect(() => {
    const userId = userService.user?.id;
    const email = userService.user?.email;
    if (!userId) return;

    const run = async () => {
      if (email) {
        const result = await reviewService.acceptPendingInvitesByEmail(email, userId);
        if (result) {
          navigate(`/plans/${result.planOwnerId}/${result.planId}`);
          return;
        }
      }
      plansService.clearPlan();
      if (plansService.loadedForUserId !== userId) {
        plansService.loadPlans(userId, true);
      }
      reviewService.loadSharedPlans(userId);
      setLoadingStarted(true);
    };

    run();
  }, [plansService, userService.user?.id, userService.user?.email, reviewService, navigate]);

  // Wait for BOTH loads to finish before deciding to redirect
  useEffect(() => {
    if (!plansService.isLoadingPlans && !reviewService.isLoading && loadingStarted) {
      if (!plansService.plans.length && !reviewService.sharedPlans.length) {
        setLoadingStarted(false);
        navigate("/plans/new");
      }
    }
  }, [plansService.isLoadingPlans, reviewService.isLoading, loadingStarted, navigate, plansService.plans.length, reviewService.sharedPlans.length]);

  // Auto-switch to shared tab when user has no own plans
  useEffect(() => {
    if (!plansService.isLoadingPlans && !reviewService.isLoading
        && plansService.plans.length === 0 && reviewService.sharedPlans.length > 0) {
      setPlanFilter('shared');
    }
  }, [plansService.isLoadingPlans, reviewService.isLoading, plansService.plans.length, reviewService.sharedPlans.length]);

  useEffect(() => {
    if (!userService.user?.id || plansService.isLoadingPlans || !plansService.plans.length) {
      return;
    }
    surveyService.checkFromPlans(userService.user.id, plansService.plans);
  }, [surveyService, userService.user?.id, plansService.isLoadingPlans, plansService.plans]);

  const handleClickPlan = (id: string) => {
    posthog.capture('plan_clicked', { planId: id });
    if (!userService.user?.id) {
      return;
    }
    navigate(`/plans/${userService.user.id}/${id}`);
  }

  const handleClickSharedPlan = (ownerId: string, planId: string) => {
    posthog.capture('shared_plan_clicked', { planId, ownerId });
    navigate(`/plans/${ownerId}/${planId}`);
  }

  const handleNewPlan = () => {
    posthog.capture('new_plan_button_clicked');
    navigate("/plans/new");
  }

  const isLoading = plansService.isLoadingPlans || reviewService.isLoading;
  const hasNoPlansYet = !plansService.plans.length && !reviewService.sharedPlans.length;
  const hasNeverLoaded = plansService.loadedForUserId === null;

  if ((isLoading || hasNeverLoaded) && hasNoPlansYet) {
    return <Box height="100vh" width="100%" display="flex" alignItems="center" justifyContent="center"><CircularProgress /></Box>;
  }

  const hasSharedPlans = reviewService.sharedPlans.length > 0;
  const hasOwnPlans = plansService.plans.length > 0;

  return (
    <>
      {surveyService.shouldShowSurvey && surveyService.planTriggeringSurvey && userService.user?.id && (
        <SurveyDialog
          open={surveyService.shouldShowSurvey}
          plan={surveyService.planTriggeringSurvey}
          isPaid={permission !== Permission.free}
          userId={userService.user.id}
          onDismiss={() => surveyService.markSurveyShown(userService.user!.id!, surveyService.planTriggeringSurvey!.id)}
          onSubmit={async (feedback) => {
            await surveyService.submitFeedback(feedback);
            setShowSurveyThankYou(true);
          }}
        />
      )}
      <Snackbar
        open={showSurveyThankYou}
        autoHideDuration={4000}
        onClose={() => setShowSurveyThankYou(false)}
        message={intl.formatMessage({ id: "survey.thankYou" })}
      />
      <Box paddingX={4} paddingTop={4} display="flex" alignItems="center" justifyContent="space-between">
        <Typography variant="h4">{intl.formatMessage({ id: "nav.plans" })}</Typography>
        {isUpMD && (
          <Box position="absolute" left="50%" sx={{ transform: 'translateX(calc(-50% + 32px))' }}>
            <Logo width={100} />
          </Box>
        )}
        <Button variant="contained" color="primary" onClick={handleNewPlan} startIcon={<AddOutlined />}>
          {intl.formatMessage({ id: "plans.newPlan" })}
        </Button>
      </Box>

      {hasSharedPlans && (
        <Box paddingX={4} paddingTop={2}>
          <ToggleButtonGroup
            value={planFilter}
            exclusive
            onChange={(_, val) => { if (val) setPlanFilter(val); }}
            size="small"
          >
            <ToggleButton value="mine">{intl.formatMessage({ id: "review.myPlans" })}</ToggleButton>
            <ToggleButton value="shared">{intl.formatMessage({ id: "review.sharedWithMe" })}</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      )}

      <Box marginTop={4}>
        {!!userService.user?.id && planFilter === 'mine' && (
          hasOwnPlans ? (
            <InfiniteScroll
              dataLength={plansService.plans.length}
              next={() => { plansService.loadPlans(userService.user!.id!); }}
              hasMore={plansService.hasMorePlans}
              scrollThreshold={1}
              loader={<Box height={100} width="100%" display="flex" alignItems="center" justifyContent="center"><CircularProgress /></Box>}
              style={{
                display: 'flex',
                flexDirection: 'row',
                flexWrap: 'wrap',
                marginTop: "-32px",
                width: "calc(100% + 32px)",
                marginLeft: "-32px",
                padding: "32px",
              }}
            >
              {plansService.plans.map((plan) => (
                <PlanItem key={plan.id} plan={plan} onClick={() => handleClickPlan(plan.id)} />
              ))}
            </InfiniteScroll>
          ) : (
            <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="50vh" gap={2}>
              <Typography variant="body1" color="text.secondary">
                {intl.formatMessage({ id: "plans.noPlans" })}
              </Typography>
              <Button variant="contained" startIcon={<AddOutlined />} onClick={handleNewPlan}>
                {intl.formatMessage({ id: "plans.newPlan" })}
              </Button>
            </Box>
          )
        )}

        {!!userService.user?.id && planFilter === 'shared' && (
          <Box
            display="flex"
            flexDirection="row"
            flexWrap="wrap"
            marginTop="-32px"
            width="calc(100% + 32px)"
            marginLeft="-32px"
            padding="32px"
          >
            {reviewService.sharedPlans.map((plan) => (
              <PlanItem key={plan.id} plan={plan} onClick={() => handleClickSharedPlan(plan.userId, plan.id)} />
            ))}
          </Box>
        )}
      </Box>
    </>
  );
});

export default Home;
